import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { callApi } from "../Helpers";
import { getInlineLoader } from "../Helpers";
import { MdReceiptLong, MdTrendingUp, MdTrendingDown, MdOutlinePerson, MdCalendarToday } from "react-icons/md";

export default function EditDebt({ uuid }) {
  const appContext = useContext(AppContext);

  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  // Master Modifiable Input Controllers
  const [durationMonths, setDurationMonths] = useState("");
  const [contractDate, setContractDate] = useState("");
  const [repaymentType, setRepaymentType] = useState("");
  const [assignedTo, setAssignedTo] = useState("");

  // Automated System-Generated Previews (Disabled Inputs)
  const [totalAmount, setTotalAmount] = useState(0);
  const [balance, setBalance] = useState(0);

  async function init() {
    setReady(false);
    await Promise.all([
      appContext.allAdmins ? Promise.resolve() : appContext.getAllAdmins(),
      getData(),
    ]);
    setReady(true);
  }

  async function getData() {
    await callApi("get-debt-data", { uuid }).then((response) => {
      if (response.status === 1) {
        setData(response.data);
      } else {
        appContext.tellError("Could not fetch debt records for modification");
      }
    });
  }

  // Reactive side effect mirroring the sales module calculation sequence precisely
  useEffect(() => {
    const monthsCount = parseInt(durationMonths);
    
    if (isNaN(monthsCount) || monthsCount <= 0) {
      setTotalAmount(0);
      setBalance(0);
      return;
    }

    const totalWeeks = Math.round(monthsCount * 4.33333);
    const calculatedTotal = totalWeeks * 70000;

    setTotalAmount(calculatedTotal);

    if (data) {
      const amountPaidAlready = Number(data.amountPaid || 0);
      setBalance(Math.max(0, calculatedTotal - amountPaidAlready));
    }
  }, [durationMonths, data]);

  async function handleUpdate() {
    if (!durationMonths || parseInt(durationMonths) <= 0) {
      appContext.tellError("Please select a valid contract duration");
      return;
    }
    if (!contractDate) {
      appContext.tellError("Please select a valid contract date");
      return;
    }

    setLoading(true);
    
    await callApi("update-debt", {
      uuid,
      durationMonths: parseInt(durationMonths),
      contractDate: contractDate,
      repaymentType,
      assignedTo: assignedTo || null,
    }).then((response) => {
      if (response.status === 1) {
        appContext.tellMessage("Debt ledger successfully re-calculated and saved");
        appContext.navBack();
        if (appContext.getAllDebts) appContext.getAllDebts();
      } else {
        appContext.tellError(response.msg || "Failed to update debt record");
      }
    });
    setLoading(false);
  }

  useEffect(() => {
    init();
  }, [uuid]);

  // Safely hydrate fields once data object loads from the DB
  useEffect(() => {
    if (data) {
      setRepaymentType(data.repaymentType ?? "Repayment plan");
      setAssignedTo(data.assignedTo ?? "");
      
      const databaseDuration = data.saleDurationMonths;
      setDurationMonths(databaseDuration ? String(databaseDuration) : "");

      // Handle raw UNIX timestamp configurations cleanly on initialization
      const rawDate = data.contractDate || data.dateCreated;
      if (rawDate) {
        let parsedDate;
        if (typeof rawDate === "number" || /^\d{10}$/.test(String(rawDate).trim())) {
          parsedDate = new Date(Number(rawDate) * 1000);
        } else {
          parsedDate = new Date(rawDate);
        }

        if (!isNaN(parsedDate.getTime())) {
          setContractDate(parsedDate.toISOString().split("T")[0]);
        } else {
          setContractDate(new Date().toISOString().split("T")[0]);
        }
      } else {
        setContractDate(new Date().toISOString().split("T")[0]);
      }
    }
  }, [data]);

  useEffect(() => {
    if (appContext.setShowOverlayLoader) {
      appContext.setShowOverlayLoader(loading);
    }
    return () => {
      if (appContext.setShowOverlayLoader) appContext.setShowOverlayLoader(false);
    };
  }, [loading]);

  if (!ready || !data) {
    return (
      <div className="container mSupportLoading">
        {getInlineLoader()}
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="app-form-page">
        <div className="app-form-shell bg-white rounded shadow-sm p-4 border">
          
          <div className="app-form-header border-bottom pb-3 mb-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h5 className="app-form-title font-bold text-primary mb-1">Edit Debt Ledger</h5>
              <p className="text-muted small mb-0 d-flex align-items-center gap-2 flex-wrap">
                <span className="badge bg-light text-dark border fw-medium px-2 py-1">{data.customerName || data.fullname}</span>
                &bull;
                <span className="badge bg-dark text-light border fw-normal px-2 py-1">{data.model}</span>
              </p>
            </div>
          </div>

          <div className="app-form-section">
            <div className="row g-4">
              
              {/* Duration Term Controller Input Card */}
              <div className="col-md-6 d-flex flex-column">
                <div className="app-form-input-wrap border rounded p-3 bg-light-subtle h-100">
                  <label className="form-label small fw-bold text-muted mb-1">Contract Duration (Months)</label>
                  <input
                    type="number"
                    className="form-control fw-bold text-primary"
                    value={durationMonths}
                    onChange={(e) => setDurationMonths(e.target.value)}
                    placeholder="Loading contract terms..."
                  />
                </div>
              </div>

              {/* Contract Date Controller Input Card */}
              <div className="col-md-6 d-flex flex-column">
                <div className="app-form-input-wrap border rounded p-3 bg-light-subtle h-100">
                  <label className="form-label small fw-bold text-muted mb-1">Contract Date</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light text-muted"><MdCalendarToday size={16} /></span>
                    <input
                      type="date"
                      className="form-control"
                      value={contractDate}
                      onChange={(e) => setContractDate(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Total Contract Amount Preview Card (Disabled) */}
              <div className="col-md-6 d-flex flex-column d-none">
                <div className="app-form-input-wrap border rounded p-3 bg-light opacity-75 h-100">
                  <label className="form-label small fw-bold text-muted mb-1">Calculated Contract Value (TZS)</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light text-muted"><MdTrendingUp size={16} /></span>
                    <input
                      type="text"
                      className="form-control border-dashed bg-transparent fw-semibold"
                      value={totalAmount > 0 ? Number(totalAmount).toLocaleString() : "—"}
                      disabled
                    />
                  </div>
                  <small className="text-success small d-block mt-2">&bull; System generated via duration weeks</small>
                </div>
              </div>

              {/* Outstanding Balance Preview Card (Disabled) */}
              <div className="col-md-6 d-flex flex-column d-none">
                <div className="app-form-input-wrap border rounded p-3 bg-light opacity-75 h-100">
                  <label className="form-label small fw-bold text-muted mb-1">Calculated Remaining Balance (TZS)</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light text-muted"><MdTrendingDown size={16} /></span>
                    <input
                      type="text"
                      className="form-control border-dashed bg-transparent fw-semibold"
                      value={totalAmount > 0 ? Number(balance).toLocaleString() : "—"}
                      disabled
                    />
                  </div>
                  <small className="text-muted small d-block mt-2">Contract amount less historically collected records.</small>
                </div>
              </div>

              {/* Account Assignment Structure Lookup Card */}
              <div className="col-md-12 d-flex flex-column">
                <div className="app-form-input-wrap border rounded p-3 bg-light-subtle h-100">
                  <label className="form-label small fw-bold text-muted mb-1">Assigned Loan Officer</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light text-muted"><MdOutlinePerson size={16} /></span>
                    <select
                      className="form-select"
                      value={assignedTo}
                      onChange={(e) => setAssignedTo(e.target.value)}
                    >
                      <option value="">Not Assigned / System Base Pool</option>
                      {(appContext.allAdmins || [])
                        .filter((admin) => {
                          const role = String(admin.role || "").toLowerCase();
                          return role.includes("collection") || role.includes("collector");
                        })
                        .map((admin) => (
                          <option key={admin.id} value={admin.id}>
                            {admin.fullname} ({admin.role || "Officer"})
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Action Buttons Footer Row */}
              <div className="col-12 mt-4 pt-3 border-top text-end app-form-submit-row">
                <button
                  type="button"
                  className="btn btn-outline-danger me-2 px-4"
                  onClick={() => appContext.navBack()}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success px-4"
                  onClick={handleUpdate}
                  disabled={loading || !durationMonths}
                >
                  {loading ? "Calculating..." : "Save Adjustments"}
                </button>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
}